import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, MapPin, Calendar, Star } from "lucide-react";

const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const fullUrl = (u) => (u && u.startsWith("/") ? `${backendOrigin}${u}` : u);

/**
 * Public full-screen project gallery lightbox.
 *
 * Props:
 *   project — full project object with { title, category, location, year,
 *             image, gallery: [{url, alt}] }. If null, component renders nothing.
 *   onClose — callback.
 *   initialIndex — start slide (default 0).
 *
 * Features: keyboard (← → Esc), backdrop click, touch swipe, thumbnail strip.
 */
export default function ProjectLightbox({ project, onClose, initialIndex = 0 }) {
  const [idx, setIdx] = useState(initialIndex);
  const touchX = useRef(null);

  // Build the canonical list of photos. Gallery has priority; fallback to
  // the single `image` field for legacy projects without gallery.
  const photos = (() => {
    if (!project) return [];
    const g = Array.isArray(project.gallery) ? project.gallery : [];
    if (g.length > 0) return g;
    if (project.image) return [{ url: project.image, alt: project.title }];
    return [];
  })();

  useEffect(() => {
    if (project) setIdx(Math.min(initialIndex, Math.max(0, photos.length - 1)));
  }, [project, initialIndex, photos.length]);

  useEffect(() => {
    if (!project) return undefined;
    const prev = () => setIdx((i) => (i === 0 ? photos.length - 1 : i - 1));
    const next = () => setIdx((i) => (i === photos.length - 1 ? 0 : i + 1));
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    // Prevent background scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [project, onClose, photos.length]);

  if (!project || photos.length === 0) return null;

  const current = photos[idx];
  const prev = () => setIdx((i) => (i === 0 ? photos.length - 1 : i - 1));
  const next = () => setIdx((i) => (i === photos.length - 1 ? 0 : i + 1));

  const onTouchStart = (e) => {
    touchX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(delta) > 60) {
      if (delta < 0) next();
      else prev();
    }
    touchX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[9000] bg-black/95 backdrop-blur-sm flex flex-col animate-fade-in"
      onClick={onClose}
      data-testid="project-lightbox"
    >
      {/* Top bar */}
      <div
        className="flex items-start justify-between p-4 md:p-6 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1 pr-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#00E5FF] mono mb-1">
            {project.category}
            {project.featured && (
              <>
                <span className="opacity-30">•</span>
                <Star size={12} className="fill-[#00E5FF]" />
                <span>Zvýraznené</span>
              </>
            )}
          </div>
          <h2 className="text-xl md:text-3xl font-black text-white truncate">
            {project.title}
          </h2>
          <div className="flex items-center gap-4 mt-1 text-xs md:text-sm text-zinc-400">
            {project.location && (
              <span className="flex items-center gap-1">
                <MapPin size={12} /> {project.location}
              </span>
            )}
            {project.year && (
              <span className="flex items-center gap-1">
                <Calendar size={12} /> {project.year}
              </span>
            )}
            <span className="text-zinc-500 mono">
              {idx + 1} / {photos.length}
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0"
          aria-label="Zavrieť"
          data-testid="project-lightbox-close"
        >
          <X size={22} />
        </button>
      </div>

      {/* Main stage */}
      <div
        className="flex-1 flex items-center justify-center relative select-none overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {photos.length > 1 && (
          <button
            onClick={prev}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 hover:text-[#00E5FF] transition-colors z-10"
            aria-label="Predchádzajúca"
            data-testid="project-lightbox-prev"
          >
            <ChevronLeft size={28} />
          </button>
        )}

        <img
          key={current.url}
          src={fullUrl(current.url)}
          alt={current.alt || (project.location ? `${project.title} — ${project.location}` : project.title)}
          className="max-w-full max-h-full object-contain px-4 md:px-20 animate-fade-in"
          draggable={false}
          data-testid="project-lightbox-image"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/placeholder-project.svg";
          }}
        />

        {photos.length > 1 && (
          <button
            onClick={next}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 hover:text-[#00E5FF] transition-colors z-10"
            aria-label="Ďalšia"
            data-testid="project-lightbox-next"
          >
            <ChevronRight size={28} />
          </button>
        )}
      </div>

      {/* Caption (alt text as caption) */}
      {current.alt && (
        <div
          className="px-4 md:px-12 pb-2 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xs md:text-sm text-zinc-400 max-w-2xl mx-auto line-clamp-2">
            {current.alt}
          </p>
        </div>
      )}

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div
          className="px-4 md:px-6 pb-4 md:pb-6 pt-3 overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="project-lightbox-thumbs"
        >
          <div className="flex gap-2 justify-center min-w-min">
            {photos.map((p, i) => (
              <button
                key={p.url}
                onClick={() => setIdx(i)}
                className={`relative w-16 h-12 md:w-20 md:h-16 rounded overflow-hidden flex-shrink-0 transition-all ${
                  i === idx
                    ? "ring-2 ring-[#00E5FF] opacity-100 scale-[1.05]"
                    : "opacity-40 hover:opacity-70"
                }`}
                aria-label={`Foto ${i + 1}`}
              >
                <img
                  src={fullUrl(p.url)}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/placeholder-project.svg";
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
